import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    // Clear any timer already running before starting another. Without this
    // the previous interval keeps ticking with its handle lost, so every extra
    // play() call stacks one more timer onto the same slideshow — the slides
    // then advance once per timer per tick and the bar visibly flickers.
    // Re-entry is easy to hit: moving the element in the DOM re-fires
    // connectedCallback (assets/sindhi-glass-hero-relocate.js relocates this
    // bar over the hero, and re-runs on section load and on resize), and the
    // theme editor re-renders sections constantly. 2026-08-02.
    this.suspend();

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   *
   * Uses suspend()/play() rather than pause()/resume(): pause() sets the
   * `paused` flag, and resume() early-returns while that flag is set, so the
   * original pause()/resume() pairing left autoplay permanently dead after the
   * very first tab switch. suspend() stops the timer without claiming the bar
   * was deliberately paused, so returning to the tab can restart it — while
   * still respecting a real pause() (e.g. a merchant/JS-initiated one).
   * 2026-08-02.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) this.suspend();
    else if (!this.paused) this.play();
  };

  disconnectedCallback() {
    super.disconnectedCallback?.();
    // Without this, relocating the bar in the DOM leaves the old timer running
    // and re-adds the visibility listener on reconnect, compounding both leaks.
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
